/**
 * Auth context object and hook. Kept separate from the provider component so
 * the provider file exports only a component (Vite fast-refresh constraint),
 * mirroring the useToast / SnackbarProvider split.
 */
import { createContext, useContext } from "react";
import type { AuthUser } from "../lib/api";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

/** Which sign-in surface the backend asks the SPA to present. */
export type AuthMode = "none" | "local" | "oidc";

export type AuthState = {
  /** Whether the backend runs an authentication mode at all. */
  enabled: boolean;
  /** `none` = no sign-in surface · `local` = email + password form ·
   *  `oidc` = a single "sign in with your organisation account" button. */
  mode: AuthMode;
  status: AuthStatus;
  user: AuthUser | null;
  isSysadmin: boolean;
  /** AUTH_MODE=local only. Under `oidc` the credentials never reach us. */
  login: (email: string, password: string) => Promise<AuthUser>;
  /** AUTH_MODE=oidc only: hands the browser to the identity provider. */
  signInWithProvider: () => Promise<void>;
  logout: () => Promise<void>;
  /** Re-read /auth/me after a profile change (e.g. clearing must_change). */
  refreshUser: () => Promise<void>;
  /** Set when `oidc` is on but the Entra values are not filled in yet, so the
   *  login page can say what is missing instead of failing silently. */
  configError: string | null;
};

export const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
